'use client';

import { useCallback, useRef } from 'react';
import type { ReactElement } from 'react';

import type { ApiListingApplicationListingType } from '#/api/types/ApiListingApplicationListingType';
import type { ApiOrgId } from '#/api/types/ApiTypedId';
import { Box } from '#/components/Box/Box';
import { useElementReachedEffect } from '#/hooks/viewport/useElementReachedEffect';
import { ListingViewAtsApplicationForm } from '#/modules/listing/components/ListingView/AtsApplicationForm/ListingViewAtsApplicationForm';
import { ApiOrgType } from '#/modules/listing/orgs/api/types/ApiOrgType';
import { trackEvent } from '#/utils/analytics/track/trackEvent';

import { ListingViewApplyAtsInstructions } from './ListingViewApplyAtsInstructions';

type Props = {
  applyText: ReactElement<typeof ListingViewApplyAtsInstructions>;
  applicationForm: ReactElement<typeof ListingViewAtsApplicationForm>;
  trackReached: {
    listingType: ApiListingApplicationListingType;
    listingId: string;
    orgId: ApiOrgId | null;
    orgType: ApiOrgType | null;
    applied: boolean;
  } | null;
};

export function ListingViewApplyAts({
  applyText,
  applicationForm,
  trackReached,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useElementReachedEffect({
    ref,
    onReach: useCallback(() => {
      if (!trackReached?.listingId) return;

      trackEvent('Section View: Idealist ATS', {
        listing_type: trackReached.listingType,
        listing_id: trackReached.listingId,
        org_id: trackReached.orgId ?? null,
        org_type: trackReached.orgType ?? null,
        has_applied: trackReached.applied,
      });
    }, [
      trackReached?.listingType,
      trackReached?.listingId,
      trackReached?.orgId,
      trackReached?.orgType,
      trackReached?.applied,
    ]),
  });

  return (
    <div ref={ref}>
      {applyText}
      <Box mt={48}>{applicationForm}</Box>
    </div>
  );
}
